"use client";

import { useRef, useState } from "react";
import type { CSSProperties, FormEvent, ReactNode } from "react";
import { buttonClass } from "./design";
import {
    siteBuilderLeadValuesFromFormData,
    submitSiteBuilderLead,
} from "./siteBuilderLeadRuntime";
import {
    templateLeadStartedAt,
} from "./templateLeadSubmission";
import type { ResolvedTemplateDesign, SiteLeadForm, SiteLeadFormField } from "./types";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

function fieldInput(field: SiteLeadFormField, readOnly: boolean) {
    const className = "site-template-lead-control mt-1 w-full rounded-[var(--site-radius)] border px-4 py-3 text-sm text-[var(--site-text)] outline-none transition";

    if (field.type === "textarea") {
        return (
            <textarea
                id={field.key}
                name={field.key}
                required={field.required}
                disabled={readOnly}
                placeholder={field.placeholder ?? undefined}
                rows={field.rows ?? 4}
                className={`${className} resize-y`}
            />
        );
    }

    if (field.type === "select") {
        return (
            <select
                id={field.key}
                name={field.key}
                required={field.required}
                disabled={readOnly}
                className={className}
            >
                <option value="">{field.placeholder?.trim() || "Vyberte možnost"}</option>
                {field.options.map((option) => (
                    <option key={option} value={option}>
                        {option}
                    </option>
                ))}
            </select>
        );
    }

    return (
        <input
            id={field.key}
            name={field.key}
            type={field.type}
            required={field.required}
            disabled={readOnly}
            placeholder={field.placeholder ?? undefined}
            className={className}
        />
    );
}

export function TemplateLeadForm({
    authoredItems,
    hasAuthoredSubmitButton = false,
    form,
    buttonBackgroundColor,
    buttonTextColor,
    columns = 1,
    design,
    fieldGap,
    formKey,
    readOnly = false,
    submitLabel,
    successMessage,
}: {
    authoredItems?: ReactNode;
    form: SiteLeadForm | null | undefined;
    hasAuthoredSubmitButton?: boolean;
    buttonBackgroundColor?: string | null;
    buttonTextColor?: string | null;
    columns?: number;
    design: ResolvedTemplateDesign;
    fieldGap?: string | null;
    formKey?: string | null;
    readOnly?: boolean;
    submitLabel?: string | null;
    successMessage?: string | null;
}) {
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const [startedAt] = useState(() => templateLeadStartedAt());
    const fieldGridStyle = {
        "--site-form-columns": String(Math.max(1, Math.min(4, Math.trunc(columns)))),
        gap: fieldGap || undefined,
    } as CSSProperties;

    const resolvedForm = form && (!formKey || form.purpose === formKey) ? form : null;

    if (!resolvedForm || resolvedForm.fields.length === 0) {
        return (
            <div
                className="site-template-lead-empty-state rounded-[var(--site-radius)] border border-dashed border-[var(--site-border)] bg-[var(--site-background)] px-4 py-3 text-sm leading-6 text-[var(--site-muted)]"
                style={{ maxWidth: "100%", minWidth: 0, overflowWrap: "anywhere" }}
            >
                Formulář se zobrazí po přiřazení veřejného formuláře k tomuto webu.
            </div>
        );
    }

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        if (!resolvedForm || readOnly || submittingRef.current) {
            return;
        }

        submittingRef.current = true;

        const formElement = event.currentTarget;

        setError("");
        setSuccess("");
        setSubmitting(true);

        try {
            const payload = siteBuilderLeadValuesFromFormData(
                new FormData(formElement),
            );
            const result = await submitSiteBuilderLead({
                endpoint: `${API_BASE}/lead/submit`,
                fields: payload,
                form: resolvedForm,
                honeypotValue: typeof payload.honeypotValue === "string"
                    ? payload.honeypotValue
                    : "",
                landingUrl: window.location.href,
                purpose: resolvedForm.purpose,
                startedAt,
            });

            if (result.ok) {
                setSuccess(
                    successMessage
                    ?? result.message
                    ?? "Děkujeme. Ozveme se vám s dalším krokem.",
                );
                formElement.reset();

                return;
            }

            setError(result.message ?? "Odeslání se nezdařilo. Zkuste to prosím znovu.");
        } catch {
            setError("Odeslání se nezdařilo. Zkuste to prosím znovu.");
        } finally {
            submittingRef.current = false;
            setSubmitting(false);
        }
    }

    return (
        <form
            aria-busy={submitting}
            onSubmit={handleSubmit}
            className="site-template-lead-form space-y-4"
            style={{ containerType: "inline-size" }}
        >
            <input type="text" name="honeypotValue" className="hidden" tabIndex={-1} autoComplete="off" />
            <style>{`
                @container (min-width: 640px) {
                    .site-template-lead-fields {
                        grid-template-columns: repeat(var(--site-form-columns, 1), minmax(0, 1fr));
                    }

                    .site-template-lead-submit {
                        width: auto;
                    }
                }

                .site-template-lead-form,
                .site-template-lead-authored-items,
                .site-template-lead-fields,
                .site-template-lead-fields > * {
                    max-width: 100%;
                    min-width: 0;
                }

                .site-template-lead-form,
                .site-template-lead-authored-items,
                .site-template-lead-fields {
                    width: 100%;
                }

                .site-template-lead-control {
                    background: color-mix(in srgb, var(--site-surface) 88%, var(--site-text) 12%);
                    border-color: color-mix(in srgb, var(--site-text) 42%, var(--site-surface));
                    box-sizing: border-box;
                    max-width: 100%;
                    min-width: 0;
                }

                .site-template-lead-control::placeholder {
                    color: color-mix(in srgb, var(--site-text) 70%, transparent);
                    opacity: 1;
                }

                .site-template-lead-control:focus {
                    border-color: var(--site-accent, var(--site-primary));
                    box-shadow: 0 0 0 4px color-mix(in srgb, var(--site-accent, var(--site-primary)) 18%, transparent);
                }

                .site-template-lead-form label,
                .site-template-lead-feedback,
                .site-template-lead-empty-state,
                .site-template-lead-submit {
                    overflow-wrap: anywhere;
                }

                .site-template-lead-submit {
                    max-width: 100%;
                    min-width: 0;
                    white-space: normal;
                }
            `}</style>

            {error !== "" ? (
                <div className="site-template-lead-feedback rounded-[var(--site-radius)] border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            ) : null}
            {success !== "" ? (
                <div className="site-template-lead-feedback rounded-[var(--site-radius)] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{success}</div>
            ) : null}

            {authoredItems !== undefined && authoredItems !== null ? (
                <fieldset
                    className="site-template-lead-authored-items"
                    disabled={submitting || readOnly}
                    style={{ border: 0, margin: 0, padding: 0 }}
                >
                    {authoredItems}
                </fieldset>
            ) : (
                <div className="site-template-lead-fields grid grid-cols-1 gap-4" style={fieldGridStyle}>
                    {resolvedForm.fields.map((field) => (
                        <div
                            key={field.key}
                            style={field.type === "textarea" ? { gridColumn: "1 / -1" } : undefined}
                        >
                            <label htmlFor={field.key} className="block text-sm font-medium text-[var(--site-text)]">
                                {field.label}
                                {field.required ? <span className="ml-1 text-[var(--site-primary)]">*</span> : null}
                            </label>
                            {fieldInput(field, readOnly)}
                        </div>
                    ))}
                </div>
            )}

            {hasAuthoredSubmitButton ? null : (
                <button
                    type="submit"
                    disabled={submitting || readOnly}
                    className={`site-template-lead-submit inline-flex w-full justify-center px-5 py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${buttonClass(design.components.buttons)}`}
                    style={{
                        backgroundColor: buttonBackgroundColor || undefined,
                        color: buttonTextColor || undefined,
                    }}
                >
                    {submitting ? "Odesílám..." : submitLabel?.trim() || "Odeslat"}
                </button>
            )}
        </form>
    );
}
